/*
 * /chat/[slug] (page)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type { ChatMessage } from "@psychology/db/client"
import { AIChatRoomWindow } from "@psychology/ai/client"
import { Container, Divider, IconButton, Page } from "@psychology/react-ui"
import { isAdmin, capitalizeFirstLetter } from "@psychology/utils"
import type { UIMessage } from "ai"
import { ArrowLeft, RefreshCw, Sparkles } from "lucide-react"
import { HeaderBanner } from "../../components/header-banner"
import { ChatLinkedEntity } from "../../features/chat/components/chat-linked-entity"
import { trpc } from "../../trpc"
import { authPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/chat/$chatRoomId")({
	component: ChatPage,
	loader: authPageLoader,
})

function ChatPage() {
	const { user, token } = Route.useLoaderData()
	const { chatRoomId } = Route.useParams()
	const { purpose = "psychology_unknown" } = Route.useSearch<any>()
	const navigate = useNavigate()

	const refreshContextMutation = trpc.chatRoom.refreshContext.useMutation()
	const processMutation = trpc.chatRoom.process.useMutation({
		onSuccess: (suggestion) => {
			navigate({
				to: `/context-suggestions/${suggestion.id}` as any,
			})
		},
	})
	const { data: pendingData } =
		trpc.contextSuggestion.getPendingCount.useQuery()

	function handleProcess() {
		if (
			pendingData?.count &&
			pendingData.count > 0 &&
			!window.confirm(
				`You have ${pendingData.count} suggestion(s) awaiting review. Creating another before reviewing those risks conflicting changes. Continue anyway?`,
			)
		) {
			return
		}
		processMutation.mutate({ id: chatRoomId })
	}

	const deleteChatMessageMutation = trpc.chatMessage.delete.useMutation()

	async function deleteChatMessage(chatMessageId: string) {
		const result = await deleteChatMessageMutation.mutateAsync({
			id: chatMessageId,
		})
		return Boolean(result)
	}

	const chatRoom = trpc.chatRoom.getByFilter.useQuery({
		where: { id: chatRoomId, userId: user.id },
	})

	const chatMessages = trpc.chatMessage.getAllByFilter.useQuery({
		pagination: { limit: 50, offset: 0 },
		sort: { column: "createdAt", direction: "asc" },
		where: { chatRoomId },
	})

	function parseChatMessagesToAIMessages(messages: ChatMessage[]): UIMessage[] {
		return messages.map(({ privateNotes, content, ...message }: any) => ({
			role: message.aiModelId ? "assistant" : "user",
			privateNotes: (isAdmin(user) && privateNotes) || undefined,
			parts: [{ type: "text", text: content }],
			...message,
		})) as UIMessage[]
	}

	if (chatRoom.isLoading || chatMessages.isLoading) {
		return (
			<Page centerVertical={true}>
				<Container width={"xl"} padding={"lg"}>
					<div className={"text-center"}>{"Loading..."}</div>
				</Container>
			</Page>
		)
	}

	const initialMessages = parseChatMessagesToAIMessages(chatMessages.data || [])

	const apiRoute = `${process.env.API_DOMAIN || ""}/api/v1/chat`

	return (
		<Page scrolling={false}>
			<HeaderBanner
				title={capitalizeFirstLetter(chatRoom.data?.title || "New chat")}
				left={{ to: "/chat", label: "Back", Icon: <ArrowLeft size={20} /> }}
				right={{
					Component: (
						<div className={"flex items-center justify-end gap-2"}>
							<IconButton
								Icon={RefreshCw}
								variant={"outline"}
								size={"sm"}
								isLoading={refreshContextMutation.isPending}
								onClick={() =>
									refreshContextMutation.mutate({ id: chatRoomId })
								}
							>
								{"Refresh Context"}
							</IconButton>
							<IconButton
								Icon={Sparkles}
								variant={"secondary"}
								size={"sm"}
								isLoading={processMutation.isPending}
								onClick={handleProcess}
							>
								{"Process"}
							</IconButton>
						</div>
					),
				}}
			/>
			<Divider />
			<ChatLinkedEntity chatRoomId={chatRoomId} />
			<AIChatRoomWindow
				className={"flex-1 overflow-auto"}
				userId={user.id}
				authToken={token}
				chatRoomId={chatRoomId}
				apiRoute={apiRoute}
				deleteChatMessage={deleteChatMessage}
				initialMessages={initialMessages}
				roomPurpose={purpose}
			/>
		</Page>
	)
}
